import React, { useEffect, useState } from "react";
import Topbar from "@/components/Topbar";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import AttachmentButton, { AttachmentChip } from "@/components/AttachmentButton";
import {
  Broadcast, PaperPlaneRight, Trash, Eye, WhatsappLogo, EnvelopeSimple,
  ChatCircle, Users, Star,
} from "@phosphor-icons/react";

const AUDIENCE_LABEL = {
  all: "Todos los clientes",
  vip: "Solo clientes VIP",
  leads_open: "Leads activos",
  custom: "Personalizado",
};

const CHANNEL_ICONS = {
  whatsapp: WhatsappLogo,
  sms: ChatCircle,
  email: EnvelopeSimple,
};

const STATUS_COLORS = {
  draft: "bg-slate-500 text-white",
  scheduled: "bg-blue-500 text-white",
  sending: "bg-amber-500 text-white animate-pulse",
  completed: "bg-emerald-500 text-white",
  cancelled: "bg-red-500 text-white",
};

export default function Campaigns() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewCampaign, setPreviewCampaign] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/campaigns");
      setItems(data);
    } catch {
      toast.error("Error cargando campañas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openPreview = async (c) => {
    setPreviewCampaign(c);
    setPreview(null);
    try {
      const { data } = await api.post(`/campaigns/${c.id}/preview`);
      setPreview(data);
    } catch {
      toast.error("Error generando preview");
    }
  };

  const sendNow = async (c) => {
    if (!window.confirm(`¿Enviar campaña "${c.name}" a ${c.total_recipients || "?"} destinatarios?`)) return;
    try {
      const { data } = await api.post(`/campaigns/${c.id}/send`);
      toast.success(`Enviados: ${data.delivered} · Fallos: ${data.failed}`);
      load();
      setPreviewCampaign(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error enviando campaña");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta campaña?")) return;
    try {
      await api.delete(`/campaigns/${id}`);
      toast.success("Campaña eliminada");
      load();
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  return (
    <>
      <Topbar title="Campañas salientes" subtitle="Envía mensajes masivos por WhatsApp, SMS o Email a tu base de clientes" />
      <div className="p-8 flex-1 overflow-y-auto min-h-0">
        <div className="max-w-6xl mx-auto space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-heading text-2xl font-bold flex items-center gap-2">
                <Broadcast size={22} className="text-primary" />
                Campañas
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Historial y programación de envíos masivos a clientes
              </p>
            </div>
            <NewCampaignDialog open={open} onOpenChange={setOpen} onCreated={load} />
          </div>

          {/* Sandbox info banner */}
          <div className="border border-amber-500/30 bg-amber-500/5 rounded-lg p-4 text-sm">
            <div className="font-semibold text-amber-600 mb-1">Modo sandbox de canales externos</div>
            <div className="text-muted-foreground text-xs leading-relaxed">
              WhatsApp/SMS/Email se registran como <strong>queued</strong> y se emiten como <code className="text-xs px-1 rounded bg-muted">campaign.message.sent</code> vía webhook.
              Conecta WhatsApp Business API o SendGrid en Ajustes para activar el envío real.
            </div>
          </div>

          {loading && <div className="text-center py-12 text-muted-foreground">Cargando…</div>}
          {!loading && items.length === 0 && (
            <div className="border border-dashed border-border rounded-lg p-12 text-center">
              <Broadcast size={40} className="mx-auto text-muted-foreground/50 mb-3" />
              <div className="font-semibold mb-1">No hay campañas todavía</div>
              <div className="text-sm text-muted-foreground mb-4">Crea tu primera campaña para llegar a leads o clientes VIP</div>
            </div>
          )}

          <div className="grid gap-3">
            {items.map((c) => {
              const CIcon = CHANNEL_ICONS[c.channel] || Broadcast;
              return (
                <div key={c.id} className="border border-border rounded-lg p-4 bg-card hover:bg-accent/40 transition-colors" data-testid={`campaign-item-${c.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <CIcon size={16} className="text-primary" weight="fill" />
                        <h3 className="font-semibold text-sm">{c.name}</h3>
                        <Badge className={STATUS_COLORS[c.status] || "bg-slate-500 text-white"}>
                          {c.status}
                        </Badge>
                      </div>
                      <div className="text-xs text-muted-foreground line-clamp-2 mb-2">{c.body}</div>
                      <div className="flex items-center gap-4 text-[11px] text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Users size={11} /> {AUDIENCE_LABEL[c.audience]} · {c.total_recipients}
                        </span>
                        {c.status === "completed" && (
                          <>
                            <span className="text-emerald-600">✓ {c.delivered} entregados</span>
                            {c.failed > 0 && <span className="text-red-500">✗ {c.failed} fallos</span>}
                          </>
                        )}
                        <span>{new Date(c.created_at).toLocaleString()}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Button size="sm" variant="outline" onClick={() => openPreview(c)} data-testid={`campaign-preview-${c.id}`}>
                        <Eye size={14} />
                      </Button>
                      {c.status === "draft" && (
                        <Button size="sm" onClick={() => sendNow(c)} className="gap-1.5" data-testid={`campaign-send-${c.id}`}>
                          <PaperPlaneRight size={14} /> Enviar
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => remove(c.id)} data-testid={`campaign-delete-${c.id}`}>
                        <Trash size={14} className="text-red-500" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Preview modal */}
      <Dialog open={!!previewCampaign} onOpenChange={(o) => !o && setPreviewCampaign(null)}>
        <DialogContent className="max-w-lg" data-testid="campaign-preview-dialog">
          <DialogHeader>
            <DialogTitle>Preview de campaña</DialogTitle>
            <DialogDescription>
              {previewCampaign?.name} — audiencia: {AUDIENCE_LABEL[previewCampaign?.audience]}
            </DialogDescription>
          </DialogHeader>
          {!preview && <div className="py-6 text-center text-muted-foreground">Cargando muestras…</div>}
          {preview && (
            <div className="space-y-3">
              <div className="text-sm">
                Total destinatarios: <span className="font-bold">{preview.total}</span>
              </div>
              <div className="text-xs text-muted-foreground">Muestra de primeros 5 mensajes renderizados:</div>
              <div className="space-y-2 max-h-72 overflow-auto">
                {preview.samples.map((s, i) => (
                  <div key={i} className="border border-border rounded-md p-3 bg-secondary/50">
                    <div className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">
                      {s.name} · {s.phone || "sin teléfono"}
                    </div>
                    <div className="text-sm whitespace-pre-wrap">{s.rendered_body}</div>
                  </div>
                ))}
                {preview.samples.length === 0 && (
                  <div className="text-sm text-muted-foreground text-center py-4">Sin destinatarios coincidentes</div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewCampaign(null)}>Cerrar</Button>
            {previewCampaign?.status === "draft" && (
              <Button onClick={() => sendNow(previewCampaign)} className="gap-1.5">
                <PaperPlaneRight size={14} /> Enviar ahora
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* -------------------- New Campaign Dialog -------------------- */
function NewCampaignDialog({ open, onOpenChange, onCreated }) {
  const [name, setName] = useState("");
  const [channel, setChannel] = useState("whatsapp");
  const [audience, setAudience] = useState("all");
  const [body, setBody] = useState("¡Hola {name}! 👋\n\n");
  const [attachments, setAttachments] = useState([]);
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setName(""); setBody("¡Hola {name}! 👋\n\n"); setAttachments([]); setAudience("all"); setChannel("whatsapp");
  };

  const submit = async () => {
    if (!name.trim() || !body.trim()) return toast.error("Nombre y mensaje son requeridos");
    setSaving(true);
    try {
      await api.post("/campaigns", {
        name,
        channel,
        audience,
        body,
        attachment_ids: attachments.map((a) => a.id),
      });
      toast.success("Campaña creada como borrador");
      reset();
      onOpenChange(false);
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error creando campaña");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button className="gap-1.5" data-testid="btn-new-campaign">
          <Broadcast size={14} /> Nueva campaña
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-xl" data-testid="new-campaign-dialog">
        <DialogHeader>
          <DialogTitle>Nueva campaña saliente</DialogTitle>
          <DialogDescription>
            Envía un mensaje personalizado a un grupo de clientes. Usa <code>{"{name}"}</code> para insertar el primer nombre.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">Nombre interno</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ej: Promo verano Corolla 2026"
              data-testid="campaign-name"
              className="mt-1"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">Canal</label>
              <Select value={channel} onValueChange={setChannel}>
                <SelectTrigger className="mt-1" data-testid="campaign-channel">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  <SelectItem value="sms">SMS</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">Audiencia</label>
              <Select value={audience} onValueChange={setAudience}>
                <SelectTrigger className="mt-1" data-testid="campaign-audience">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los clientes</SelectItem>
                  <SelectItem value="vip">Solo VIP <Star className="inline" size={11} /></SelectItem>
                  <SelectItem value="leads_open">Leads activos</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">Mensaje</label>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={5}
              placeholder="¡Hola {name}! Tenemos ofertas especiales..."
              data-testid="campaign-body"
              className="mt-1 font-mono text-sm"
            />
            <div className="text-[10px] text-muted-foreground mt-1">
              Tokens disponibles: <code>{"{name}"}</code>, <code>{"{full_name}"}</code>, <code>{"{phone}"}</code>
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2 block">
              Adjuntos (PDF cotizaciones, imágenes)
            </label>
            <div className="flex items-center gap-2 flex-wrap">
              <AttachmentButton onUploaded={(f) => setAttachments((p) => [...p, f])} />
              {attachments.map((f) => (
                <AttachmentChip
                  key={f.id}
                  file={f}
                  onRemove={() => setAttachments((p) => p.filter((x) => x.id !== f.id))}
                />
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={submit} disabled={saving} data-testid="campaign-save">
            {saving ? "Guardando…" : "Crear borrador"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
